import { UserCoverStyle, Collection } from "@authory-packages/core";
import { getCollectionOGDisplayImageUrl } from "../../utils/getCollectionDisplayImageUrl";
import { V3IconCollections, V3IconGrab } from "../Icons";
import { VerticalSpacing } from "../VerticalSpacing";
import {
    CCWrapper,
    CCPreviewImagesWrapper,
    HeadLineFavWrapper,
    CCCardGrabPos,
    CCCardDescription,
    CCCardTitle,
    CollectionCardBottomInfoWrapper,
    CCCardDescriptionPlaceholder,
    CCPreviewIconPlacer
} from "./CollectionList.styles";
import { decodeEntities } from "../../utils/decodeHTMLEntities";
import { DefaultCollectionTooltip } from "../ReusableStyledComponents/DefaultCollectionTooltip";
import { OptionalNextLink } from "../ReusableStyledComponents/OptionalNextLink";
import { RequestHost } from "../../types/headers";
import { useContext } from "react";
import { DomainContext } from "../../context/domainContext";
import { isMainDomain } from "../../utils/domainRouting";
import { V3BodyGrey48 } from "../ReusableStyledComponents";
import { NO_DESCRIPTION_SET_COLLECTION_LABEL } from "../../utils/noDescriptionSetCollectionLabel";
import { CustomDarkShade } from "../ReusableStyledComponents/FontSetUtilities";
import { getCollectionUrl } from "./CollectionCard.utils";

interface CollectionCardProps {
    collection: Collection;
    isPrivateProfilePage: boolean;
    isProfilePage: boolean;
    isSorting?: boolean;
    authorSlug?: string;
    forceHover?: boolean;
    allowUserThemable?: boolean;
    smartShareUrl?: string;
}

export const CollectionCard = ({
    collection,
    isPrivateProfilePage = false,
    isProfilePage = false,
    isSorting = false,
    forceHover = false,
    smartShareUrl,
    authorSlug,
    allowUserThemable = false
}: CollectionCardProps) => {
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;
    const imageURL = getCollectionOGDisplayImageUrl(collection);
    const publicPageUrl = getCollectionUrl(authorSlug!, isMainDomain(requestHostHeader), collection.canonicalSlug, smartShareUrl);
    const DescToUse = !!collection.description ? CCCardDescription : CCCardDescriptionPlaceholder;
    const isPublicProfilePage = isProfilePage && !isPrivateProfilePage;

    return (
        <div key={collection.canonicalSlug}>
            <OptionalNextLink
                href={
                    isPublicProfilePage
                        ? // Public Profile page - use public url
                          publicPageUrl
                        : // Private profile - use nothing, it's a context menu here
                          undefined
                }
                useNextRouter={true}
            >
                <CCWrapper
                    $isSorting={isSorting}
                    $isAddCTA={false}
                    $forceHover={forceHover}
                    $isProfile={isProfilePage}
                    $skipUserThemable={!allowUserThemable}
                >
                    <VerticalSpacing bottom={18}>
                        <CCPreviewImagesWrapper
                            $imageLoaded={true}
                            $image={imageURL}
                            $color={collection.coverStyle === UserCoverStyle.color && collection.coverColor ? collection.coverColor : undefined}
                        >
                            {(collection.ogCoverStyle === UserCoverStyle.none || collection.ogCoverStyle === null) &&
                                (collection.coverStyle === UserCoverStyle.none || collection.coverStyle === null) && (
                                    <CCPreviewIconPlacer>
                                        <V3IconCollections width="36" height="36" />
                                    </CCPreviewIconPlacer>
                                )}
                        </CCPreviewImagesWrapper>
                    </VerticalSpacing>
                    <VerticalSpacing bottom={18}>
                        <HeadLineFavWrapper>
                            <div>
                                <CCCardTitle $numLines={1} $allowUserThemable={allowUserThemable}>
                                    {decodeEntities(collection.label)}
                                </CCCardTitle>
                            </div>
                            <div>{collection.default && !isPublicProfilePage ? <DefaultCollectionTooltip /> : <></>}</div>
                        </HeadLineFavWrapper>
                    </VerticalSpacing>
                    <VerticalSpacing bottom={isProfilePage ? 18 : 32}>
                        <DescToUse $numLines={2} $allowUserThemable={allowUserThemable} $customDarkShade={CustomDarkShade.Grey48}>
                            {collection.description ? collection.description : isPublicProfilePage ? "" : NO_DESCRIPTION_SET_COLLECTION_LABEL}
                        </DescToUse>
                    </VerticalSpacing>
                    {!isProfilePage && (
                        <CollectionCardBottomInfoWrapper>
                            <div>
                                <V3BodyGrey48>{`${collection.articleCount.toLocaleString()} content ${
                                    collection.articleCount === 1 ? "item" : "items"
                                }`}</V3BodyGrey48>
                            </div>
                        </CollectionCardBottomInfoWrapper>
                    )}
                    {isSorting && (
                        <CCCardGrabPos>
                            <V3IconGrab />
                        </CCCardGrabPos>
                    )}
                </CCWrapper>
            </OptionalNextLink>
        </div>
    );
};
